import {
	Component,
	OnInit,
	Input,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	inject,
} from '@angular/core';
import { Router, NavigationEnd, RoutesRecognized } from '@angular/router';
import { JepBasicPlanNamePipe } from '@jepto/shared/pipes/jep-reporting-plan-name.pipe';
import { Store } from '@ngrx/store';
import * as fromSharedStore from '@jepto/shared/store/index';
import * as fromSharedAccountStore from '@jepto/shared/store/account/index';

import { Subject, Observable } from 'rxjs';
import { filter, takeUntil, pairwise } from 'rxjs/operators';

import { sortKeySplitter } from '@jepto/shared/utils/collection';
import { getDiffTrialEndCurrentLocalTime } from '@jepto/shared/utils/date';
import dayjs from '@jepto/config/dayjs.config';

import { markForCheckOnEmit } from '@jepto/shared/utils/change-detection';
@Component({
	selector: 'jep-container-layout',
	templateUrl: './container-layout.component.html',
	styleUrls: ['./container-layout.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ContainerLayoutComponent implements OnInit {
	private readonly cdr = inject(ChangeDetectorRef);
	constructor(
		private sharedStore: Store<fromSharedStore.SharedModuleReducers>,
		private router: Router,
		private jepBasicPlanNamePipe: JepBasicPlanNamePipe
	) {}

	@Input() layout: string;
	showIndicator: boolean = false;
	showLink: boolean = true;
	subscriptionIndicatorMessage: string =
		'Your trial of the Ageny Starter Plan ends in 0 day.';
	subscriptionLinkText: string = 'Subscribe now';
	subscriptionTheme: string = 'default';
	hasCard$: Observable<void>;
	unsubscribe$ = new Subject<void>();
	account: any;
	noContentNav: boolean = false;
	isSignUpWithCoupon: boolean = false;
	accountDetails: fromSharedAccountStore.IAccount;
	contactSupport: boolean = false;

	ngOnInit() {
		this.listenOnGetAccount();
		this.checkIfNoContentNav();
		this.listenToRouterEvents();
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	listenToRouterEvents(): void {
		this.router.events
			.pipe(
				filter((evt: any) => evt instanceof RoutesRecognized),
				pairwise(),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((events: RoutesRecognized[]) => {
				if (
					!Boolean(
						this.checkQuerySring(
							events[0].urlAfterRedirects,
							'code'
						)
					) &&
					Boolean(this.accountDetails)
				) {
					if (
						events[0].urlAfterRedirects === '/sign-up' ||
						events[0].urlAfterRedirects === '/sign-in'
					) {
						this.onGetAccount(this.accountDetails);
					}
				}
			});
	}

	checkQuerySring(url: string, key: string) {
		if (url.includes('?')) {
			const queryStings = sortKeySplitter(url, '?', 1).split('&'),
				queryString = queryStings.filter(item => {
					return item.includes(key);
				});
			return sortKeySplitter(queryString[0], '=', 1);
		} else {
			return null;
		}
	}

	listenOnGetAccount() {
		this.sharedStore
			.select(fromSharedAccountStore.getAccountData)
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe((account: fromSharedAccountStore.IAccount) => {
				if (Boolean(account)) {
					this.accountDetails = account;
					this.onGetAccount(this.accountDetails);
				}
			});
	}

	onGetAccount(account: any) {
		if (account.status !== 'active') {
			if (Boolean(account.subscription)) {
				if (account.status === 'non_renewing') {
					let now = dayjs();
					let end = dayjs(account.subscription.currentTermEnd);
					let remaining = end.diff(now, 'days');
					this.subscriptionIndicatorMessage = `Your subscription has been canceled and you will lose access in ${remaining} ${remaining >= 2 ? 'days' : 'day'}. To keep your plan active please contact support.`;
					this.subscriptionLinkText = null;
					this.contactSupport = true;
					this.showLink = false;
					this.showIndicator = true;
					this.subscriptionTheme = 'default';
					return;
				}
				if (Boolean(account.hasCard)) {
					if (Boolean(account.transactionStatus)) {
						if (
							account.transactionStatus.toLowerCase() === 'failed'
						) {
							this.runExpiredCardState();
						} else {
							if (
								account.subscription.cardStatus.toLowerCase() ===
									'expired' ||
								account.subscription.cardStatus.toLowerCase() ===
									'expiring'
							) {
								this.runExpiringCardState();
							}
						}
					} else {
						if (Boolean(account.subscription.cardStatus)) {
							if (
								account.subscription.cardStatus.toLowerCase() ===
									'expired' ||
								account.subscription.cardStatus.toLowerCase() ===
									'expiring'
							) {
								this.runExpiringCardState();
							}
						} else {
							this.showIndicator = false;
						}
					}
				} else {
					this.checkTrialExpiring(account);
				}
			}
		} else {
			this.showIndicator = false;
		}
	}

	checkTrialExpiring(account: any) {
		let plan = this.jepBasicPlanNamePipe.transform(
			account.subscription.planName
		);
		if (Boolean(account.subscription.dataWarehouseType)) {
			if (account.subscription.dataWarehouseType === 'GMB_PRO') {
				plan = 'GMB Pro';
			}
			if (account.subscription.dataWarehouseType === 'GSC_PRO') {
				plan = 'GSC Pro';
			}
		}
		// let dateNow = dayjs(getDateNowUTC());
		if (account.subscription.trialEnd) {
			let diff = getDiffTrialEndCurrentLocalTime(
				account.subscription.trialEnd,
				this.accountDetails.timezone,
				'days'
			);
			console.log('checkTrialExpiring diff', diff);
			// let expDate = dayjs(account.subscription.trialEnd);
			// let tmpDiff = Number(expDate.diff(dateNow, "days", true));
			// let diff = Math.ceil(tmpDiff);
			if (!account.hasCard) {
				if (diff > 0) {
					let textDays = diff === 1 ? 'day' : 'days';
					this.subscriptionIndicatorMessage = `Your trial of the ${plan} Plan ends in ${diff} ${textDays}.`;
					this.showLink = true;
					this.showIndicator = true;
					this.subscriptionLinkText = 'Subscribe now';
					this.subscriptionTheme = 'default';
					this.contactSupport = false;
				} else {
					this.subscriptionIndicatorMessage =
						'Your trial of the ' +
						plan +
						' Plan has ended. Please contact help@jepto.com.';
					this.showLink = true;
					this.showIndicator = true;
					this.subscriptionLinkText = 'Subscribe now';
					this.subscriptionTheme = 'default';
					this.contactSupport = false;
				}
			} else {
				this.showIndicator = false;
			}
		} else {
			this.showIndicator = false;
		}
	}

	checkIfNoContentNav() {
		this.router.events
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(val => {
				if (val instanceof NavigationEnd) {
					const currentUrl = val.url;

					if (
						currentUrl.includes('anomalies') ||
						currentUrl.includes('automation') ||
						currentUrl.includes('budget') ||
						currentUrl.includes('kpis') ||
						currentUrl.includes('insights') ||
						currentUrl.includes('extensions') ||
						currentUrl.includes('analysis') ||
						currentUrl.includes('pipelines') ||
						currentUrl.includes('settings') ||
						currentUrl.includes('clients') ||
						currentUrl.includes('local') ||
						currentUrl.includes('reviews')
					) {
						this.noContentNav = true;
					} else {
						this.noContentNav = false;
					}
				}
			});
	}

	runExpiringCardState(): void {
		this.subscriptionIndicatorMessage =
			'Please update your card details prior to your next payment.';
		this.subscriptionLinkText = 'Update payment method';
		this.showLink = true;
		this.showIndicator = true;
		this.subscriptionTheme = 'default';
		this.contactSupport = false;
	}

	runExpiredCardState(): void {
		this.subscriptionIndicatorMessage = 'Your payment method failed.';
		this.subscriptionLinkText = 'Update payment method';
		this.showLink = true;
		this.showIndicator = true;
		this.subscriptionTheme = 'critical';
		this.contactSupport = false;
	}
}
